import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS, ROUTE_IDS } from '../routes/ids.js';
import { getRouteById } from '../routes/effective-routes.js';
export { PORTFOLIO_PERFORMANCE_TABLE } from './portfolio-performance-table-registry.js';
import { PORTFOLIO_PERFORMANCE_TABLE as _PORTFOLIO_PERFORMANCE_TABLE } from './portfolio-performance-table-registry.js';
import { TAX_RULE_TABLE } from './tax-rule-descriptors.js';
import { FEE_RULE_TABLE } from './fee-rule-descriptors.js';
import { ACCOUNTING_JOURNAL_POST, ACCOUNTING_JOURNAL_REVERSE, TRANSACTION_POST, TRANSACTION_REPAIR, TRANSACTION_REJECT, TRANSACTION_REPOST, TRANSACTION_SUBMIT, TRANSACTION_WITHDRAW, } from '../../operations/operation-ids.js';
const tk = (value) => value;
const telemetry = (value) => value;
const query = (value) => value;
function metadata(id, module, feature, privacyClassification = 'INTERNAL') {
    return {
        id,
        schemaVersion: 1,
        module,
        feature,
        privacyClassification,
        telemetryId: telemetry(`experience.table.${id}`),
        accessibilityLabelKey: tk(`a11y.${id}`),
        availability: 'AVAILABLE',
        owner: 'experience-registry',
    };
}
function routePath(routeId, rowField = 'id') {
    const route = getRouteById(routeId);
    if (!route)
        throw new Error(`Unknown canonical action route: ${String(routeId)}`);
    return route.path
        .replace(/\{[A-Za-z0-9_-]+\}/, `{${rowField}}`)
        .replace(/:[A-Za-z0-9_-]+/, `:${rowField}`);
}
function lifecycleEditAction(id, table, operationId, routeId, order = 10) {
    return {
        ...metadata(id, table.module, table.feature, table.privacyClassification),
        operationId,
        labelKey: 'action.edit',
        iconKey: 'edit',
        kind: 'navigate',
        intent: 'primary',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: routeId,
        successNavigateTo: routePath(routeId, table.rowIdField ?? 'id'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        order,
        group: 'navigation',
    };
}
function lifecycleCreateAction(id, module, feature, privacyClassification, operationId, routeId, order = 5) {
    return {
        ...metadata(id, module, feature, privacyClassification),
        operationId,
        labelKey: 'action.create',
        iconKey: 'add',
        kind: 'navigate',
        intent: 'primary',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: routeId,
        successNavigateTo: routePath(routeId),
        errorBehavior: 'SHOW_INLINE_ERROR',
        order,
        group: 'primary',
    };
}
/** Opens the one governed import workflow with a descriptor-owned source type. */
export function importPageAction(id, module, feature, privacyClassification, importType, order = 20) {
    return {
        ...metadata(id, module, feature, privacyClassification),
        operationId: OPERATION_IDS.IMPORT_UPLOAD,
        labelKey: 'action.import',
        iconKey: 'upload',
        kind: 'navigate',
        intent: 'secondary',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: ROUTE_IDS.DATA_IMPORT_UPLOAD,
        successNavigateTo: `${routePath(ROUTE_IDS.DATA_IMPORT_UPLOAD)}?importType=${encodeURIComponent(importType)}`,
        errorBehavior: 'SHOW_INLINE_ERROR',
        order,
        group: 'secondary',
    };
}
function lifecycleDeleteAction(id, table, operationId, commandId, order = 20) {
    return {
        ...metadata(id, table.module, table.feature, table.privacyClassification),
        operationId,
        labelKey: 'action.delete',
        iconKey: 'delete',
        kind: 'command',
        method: 'DELETE',
        commandId,
        intent: 'danger',
        idempotencyRequired: true,
        confirmation: {
            titleKey: 'common:confirmAction',
            messageKey: 'common:confirmAction',
            confirmLabelKey: 'action.delete',
            cancelLabelKey: 'action.cancel',
        },
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: 'common:success',
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: 'common:error.unexpected',
        order,
        group: 'danger',
    };
}
function column(module, feature, id, overrides = {}) {
    return {
        ...metadata(id, module, feature),
        // Columns use a11y.col.${id} to avoid collisions with a11y.* namespace
        // parents (e.g. a11y.action is both a namespace and a column id).
        accessibilityLabelKey: tk(`a11y.col.${id}`),
        labelKey: tk(`col.${id}`),
        field: id,
        fieldType: 'text',
        sortable: false,
        filterable: false,
        // Generic resource bindings project scalar allow-listed filters. Keep the
        // backend descriptor honest about the operator the shared adapter can
        // execute; specialized descriptors may opt into a broader operator set.
        allowedFilterOperators: ['equals'],
        searchable: false,
        groupable: false,
        hideable: true,
        defaultVisible: true,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: 150,
        minWidth: 80,
        maxWidth: 500,
        align: 'left',
        nullDisplayBehavior: 'dash',
        ...overrides,
    };
}
const standardCapabilities = {
    sorting: true,
    filtering: true,
    globalSearch: true,
    grouping: false,
    aggregation: false,
    pagination: true,
    selection: false,
    columnVisibility: true,
    columnResizing: true,
    columnReordering: true,
    columnPinning: false,
    savedViews: false,
    export: true,
    refresh: true,
    density: true,
};
export const ORGANIZATION_TABLE = {
    ...metadata('table.admin.organizations', MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION),
    titleKey: tk('page.organization.title'),
    dataSourceId: query('query.admin.organizations.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'id', {
            sortable: true,
            defaultVisible: false,
            defaultWidth: 120,
            privacyClassification: 'INTERNAL',
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'code', {
            sortable: true,
            searchable: true,
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'name', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 260,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'legalName', {
            sortable: true,
            searchable: true,
            defaultVisible: false,
            defaultWidth: 260,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'jurisdiction', {
            sortable: true,
            filterable: true,
            searchable: true,
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'status', {
            filterable: true,
            fieldType: 'status',
            defaultWidth: 130,
            enumOptions: ['ACTIVE', 'INACTIVE', 'SUSPENDED'].map((value) => ({
                value,
                labelKey: tk(`enum.orgStatus.${value}`),
            })),
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'assignedModules', {
            searchable: true,
            defaultWidth: 220,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'branchCount', {
            fieldType: 'number',
            sortable: true,
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, 'createdAt', {
            fieldType: 'datetime',
            sortable: true,
            defaultVisible: false,
            defaultWidth: 180,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'name', direction: 'asc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: true },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
    },
    rowActions: [
        {
            ...metadata('action.organizationProfile.edit', MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION),
            operationId: OPERATION_IDS.ORGANIZATION_PROFILE_UPDATE,
            labelKey: tk('action.edit'),
            iconKey: 'edit',
            kind: 'navigate',
            intent: 'primary',
            idempotencyRequired: false,
            successBehavior: 'NAVIGATE_TO',
            errorBehavior: 'SHOW_INLINE_ERROR',
            targetRouteId: ROUTE_IDS.ORGANIZATION_PROFILE_EDIT,
            successNavigateTo: routePath(ROUTE_IDS.ORGANIZATION_PROFILE_EDIT),
            order: 10,
            group: 'primary',
        },
    ],
};
export const USERS_TABLE = {
    ...metadata('table.admin.users', MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'CONFIDENTIAL'),
    titleKey: tk('page.users.title'),
    dataSourceId: query('query.admin.users.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'displayName', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 220,
            privacyClassification: 'PII',
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'firstName', {
            labelKey: tk('col.firstName'),
            defaultVisible: false,
            defaultWidth: 140,
            privacyClassification: 'PII',
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'lastName', {
            labelKey: tk('col.lastName'),
            defaultVisible: false,
            defaultWidth: 140,
            privacyClassification: 'PII',
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'email', {
            sortable: true,
            filterable: true,
            searchable: true,
            defaultWidth: 260,
            privacyClassification: 'PII',
            // Column-level PII gate: personas without this permission lose the column
            // in the resolved descriptor and the field in generic row payloads.
            permissionRequirement: 'organization:users:email:view',
            exportConfig: { exportable: true, privacyClassification: 'PII', redactionPolicy: 'mask' },
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'membershipStatus', {
            labelKey: tk('col.status'),
            filterable: true,
            fieldType: 'status',
            defaultWidth: 130,
            enumOptions: ['ACTIVE', 'INACTIVE', 'SUSPENDED'].map((value) => ({
                value,
                labelKey: tk(`enum.userStatus.${value}`),
            })),
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'status', {
            labelKey: tk('col.membershipStatus'),
            defaultVisible: false,
            defaultWidth: 130,
            enumOptions: ['ACTIVE', 'INACTIVE', 'SUSPENDED'].map((value) => ({
                value,
                labelKey: tk(`enum.userStatus.${value}`),
            })),
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'mfaEnabled', {
            fieldType: 'boolean',
            align: 'center',
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'roles', {
            // The list data source returns an array of role display names; a text
            // renderer joins them. 'reference' would require a referenceDescriptor +
            // id/label objects and render '—' for the name array, which is why the
            // column previously showed a dash for every user.
            fieldType: 'text',
            defaultWidth: 220,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'branchName', {
            sortable: true,
            filterable: true,
            searchable: true,
            defaultWidth: 160,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_USERS, 'lastLoginAt', {
            fieldType: 'datetime',
            sortable: true,
            defaultVisible: false,
            defaultWidth: 180,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'displayName', direction: 'asc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    selectionMode: 'multiple',
    capabilities: { ...standardCapabilities, selection: true, create: true },
    toolbar: {
        showResultCount: true,
        showSelectedCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
        showBulkActions: true,
    },
    privacyAndExportPolicy: {
        allowedExportFormats: ['csv'],
        redactFields: ['email'],
        auditExports: true,
    },
};
export const ROLES_TABLE = {
    ...metadata('table.admin.roles', MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ROLES),
    titleKey: tk('page.roles.title'),
    dataSourceId: query('query.admin.roles.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ROLES, 'name', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 220,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ROLES, 'description', {
            searchable: true,
            defaultWidth: 260,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ROLES, 'userTypeId', {
            labelKey: tk('col.userType'),
            filterable: true,
            fieldType: 'select',
            defaultWidth: 180,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ROLES, 'status', {
            filterable: true,
            fieldType: 'status',
            defaultWidth: 130,
            enumOptions: ['ACTIVE', 'INACTIVE'].map((value) => ({
                value,
                labelKey: tk(`enum.roleStatus.${value}`),
            })),
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ROLES, 'isSystem', {
            fieldType: 'boolean',
            align: 'center',
            defaultWidth: 110,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'name', direction: 'asc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    selectionMode: 'multiple',
    capabilities: { ...standardCapabilities, selection: true, create: true },
    toolbar: {
        showResultCount: true,
        showSelectedCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
    },
};
export const AUDIT_TABLE = {
    ...metadata('table.admin.audit', MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_AUDIT, 'CONFIDENTIAL'),
    titleKey: tk('page.audit.title'),
    dataSourceId: query('query.admin.audit.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_AUDIT, 'createdAt', {
            labelKey: tk('admin:auditLog.table.time'),
            sortable: true,
            fieldType: 'datetime',
            requiredVisible: true,
            defaultWidth: 190,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_AUDIT, 'entityType', {
            labelKey: tk('admin:auditLog.table.entity'),
            filterable: true,
            fieldType: 'select',
            defaultWidth: 170,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_AUDIT, 'entityId', {
            defaultVisible: false,
            privacyClassification: 'CONFIDENTIAL',
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_AUDIT, 'action', {
            labelKey: tk('admin:auditLog.table.action'),
            filterable: true,
            fieldType: 'select',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_AUDIT, 'userEmail', {
            labelKey: tk('admin:auditLog.table.user'),
            filterable: true,
            searchable: true,
            defaultWidth: 260,
            privacyClassification: 'PII',
            // Column-level PII gate: personas without this permission lose the column
            // in the resolved descriptor and the field in generic row payloads.
            permissionRequirement: 'organization:audit:user-email:view',
            exportConfig: { exportable: true, privacyClassification: 'PII', redactionPolicy: 'mask' },
        }),
    ],
    pagination: { pageSize: 50, pageSizeOptions: [25, 50, 100] },
    defaultSort: { field: 'createdAt', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: false, selection: false, create: false },
    toolbar: {
        showResultCount: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
    },
    privacyAndExportPolicy: {
        allowedExportFormats: ['csv'],
        redactFields: ['userEmail'],
        auditExports: true,
    },
};
export const INSTRUMENT_TABLE = {
    ...metadata('table.data.instruments', MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS),
    operationId: OPERATION_IDS.INSTRUMENT_LIST,
    titleKey: tk('page.instruments.title'),
    dataSourceId: query('query.data.instruments.list'),
    // Instrument detail/edit routes are keyed by the public symbol, while the
    // list payload also contains an internal database id. The route contract
    // must use the same stable key as the typed hook and API endpoint.
    rowIdField: 'symbol',
    columns: [
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'symbol', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'instrumentName', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 260,
        }),
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'securityName', {
            sortable: true,
            filterable: true,
            searchable: true,
            defaultWidth: 220,
        }),
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'instrumentType', {
            filterable: true,
            fieldType: 'select',
            defaultWidth: 170,
            enumOptions: ['EQUITY', 'DEBT', 'MUTUAL_FUND', 'PROMISSORY_NOTE'].map((value) => ({
                value,
                labelKey: tk(`enum.instrumentType.${value}`),
            })),
        }),
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'isin', {
            sortable: true,
            filterable: true,
            searchable: true,
        }),
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'currency', {
            sortable: true,
            filterable: true,
            defaultWidth: 110,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'exchange', {
            sortable: true,
            filterable: true,
        }),
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'sector', {
            sortable: true,
            filterable: true,
            allowedFilterOperators: ['equals'],
        }),
        column(MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'status', {
            filterable: true,
            fieldType: 'status',
            defaultWidth: 120,
            enumOptions: ['ACTIVE', 'INACTIVE', 'DELISTED', 'SUSPENDED'].map((value) => ({
                value,
                labelKey: tk(`enum.instrumentStatus.${value}`),
            })),
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    defaultSort: { field: 'symbol', direction: 'asc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: true },
    createAction: lifecycleCreateAction('action.data.instruments.create', MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'CONFIDENTIAL', OPERATION_IDS.INSTRUMENT_CREATE, ROUTE_IDS.DATA_INSTRUMENT_CREATE),
    pageActions: [
        importPageAction('action.data.instruments.import', MODULE_KEYS.INSTRUMENTS, FEATURE_KEYS.DATA_INSTRUMENTS, 'INTERNAL', 'INSTRUMENT'),
    ],
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
    },
    transfer: {
        import: {
            definitionId: 'INSTRUMENT',
            autoDetection: true,
            templatesSupported: true,
        },
        export: {
            formats: ['csv', 'xlsx'],
            scopes: ['all', 'filtered', 'selected'],
        },
    },
};
INSTRUMENT_TABLE.rowActions = [
    lifecycleEditAction('action.data.instruments.edit', INSTRUMENT_TABLE, OPERATION_IDS.INSTRUMENT_UPDATE, ROUTE_IDS.DATA_INSTRUMENT_EDIT),
];
export const POSITION_TRANSFER_TABLE = {
    ...metadata('table.investment.positionTransfers', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'FINANCIAL'),
    operationId: OPERATION_IDS.POSITION_TRANSFERS_LIST,
    titleKey: tk('card.positionTransfers.title'),
    dataSourceId: query('query.investment.positionTransfers.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'businessReference', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 180,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'instrumentSymbol', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 140,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'sourcePortfolioAccountId', 
        // P1-5: persistence ID hidden by default — use sourcePortfolioAccount label for display
        { defaultWidth: 190, defaultVisible: false, filterable: true }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'destinationPortfolioAccountId', { defaultWidth: 190, defaultVisible: false, filterable: true }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'status', {
            filterable: true,
            fieldType: 'status',
            defaultWidth: 130,
            requiredVisible: true,
            enumOptions: ['REQUESTED', 'APPROVED', 'EXECUTED', 'CANCELLED', 'REVERSED'].map((value) => ({
                value,
                labelKey: tk(`enum.positionTransferStatus.${value}`),
            })),
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'reason', {
            defaultWidth: 220,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'createdAt', {
            sortable: true,
            fieldType: 'datetime',
            defaultWidth: 180,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'createdAt', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: false },
    rowActions: [
        {
            ...metadata('action.investment.positionTransfers.approve', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'FINANCIAL'),
            operationId: OPERATION_IDS.POSITION_TRANSFERS_APPROVE,
            labelKey: tk('card.positionTransfers.actions.approve'),
            iconKey: 'approve',
            kind: 'command',
            commandId: 'cmd.investment.positionTransfers.approve',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common.success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common.error.unexpected'),
            order: 10,
            group: 'primary',
        },
        {
            ...metadata('action.investment.positionTransfers.execute', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'FINANCIAL'),
            operationId: OPERATION_IDS.POSITION_TRANSFERS_EXECUTE,
            labelKey: tk('card.positionTransfers.actions.execute'),
            iconKey: 'execute',
            kind: 'command',
            commandId: 'cmd.investment.positionTransfers.execute',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common.success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common.error.unexpected'),
            order: 20,
            group: 'primary',
        },
        {
            ...metadata('action.investment.positionTransfers.cancel', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'FINANCIAL'),
            operationId: OPERATION_IDS.POSITION_TRANSFERS_CANCEL,
            labelKey: tk('card.positionTransfers.actions.cancel'),
            iconKey: 'cancel',
            kind: 'command',
            commandId: 'cmd.investment.positionTransfers.cancel',
            intent: 'danger',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common.success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common.error.unexpected'),
            order: 30,
            group: 'danger',
        },
        {
            ...metadata('action.investment.positionTransfers.reverse', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'FINANCIAL'),
            operationId: OPERATION_IDS.POSITION_TRANSFERS_REVERSE,
            labelKey: tk('card.positionTransfers.actions.reverse'),
            iconKey: 'reverse',
            kind: 'command',
            commandId: 'cmd.investment.positionTransfers.reverse',
            intent: 'danger',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common.success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common.error.unexpected'),
            order: 40,
            group: 'danger',
        },
    ],
    pageActions: [
        {
            ...metadata('action.investment.positionTransfers.request', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITION_TRANSFERS, 'FINANCIAL'),
            operationId: OPERATION_IDS.POSITION_TRANSFERS_REQUEST,
            labelKey: tk('card.positionTransfers.actions.request'),
            iconKey: 'add',
            kind: 'command',
            commandId: 'cmd.investment.positionTransfers.request',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common.success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common.error.unexpected'),
            order: 5,
            group: 'primary',
        },
    ],
};
export const SETTLEMENT_INSTRUCTION_TABLE = {
    ...metadata('table.settlement.instructions', MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'FINANCIAL'),
    operationId: OPERATION_IDS.SETTLEMENT_INSTRUCTIONS_LIST,
    titleKey: tk('card.settlementInstructions.title'),
    dataSourceId: query('query.settlement.instructions.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'transactionId', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 180,
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'status', {
            filterable: true,
            fieldType: 'status',
            defaultWidth: 140,
            requiredVisible: true,
            enumOptions: [
                'PENDING',
                'PARTIALLY_SETTLED',
                'SETTLED',
                'FAILED',
                'CANCELLED',
                'CORRECTED',
            ].map((value) => ({ value, labelKey: tk(`enum.settlementInstructionStatus.${value}`) })),
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'expectedQuantity', {
            fieldType: 'decimal',
            align: 'right',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'settledQuantity', {
            fieldType: 'decimal',
            align: 'right',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'expectedCash', {
            fieldType: 'money',
            align: 'right',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'settledCash', {
            fieldType: 'money',
            align: 'right',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'currency', {
            defaultWidth: 100,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'expectedDate', {
            sortable: true,
            fieldType: 'date',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'externalReference', {
            defaultWidth: 180,
        }),
        column(MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'failureReason', {
            defaultWidth: 200,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'expectedDate', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: false },
    rowActions: [
        {
            ...metadata('action.settlement.instructions.confirm', MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'FINANCIAL'),
            operationId: OPERATION_IDS.SETTLEMENT_INSTRUCTIONS_CONFIRM,
            labelKey: tk('card.settlement.actions.confirm'),
            iconKey: 'confirm',
            kind: 'command',
            commandId: 'cmd.settlement.confirm',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('card.settlement.saved'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('card.settlement.requestFailed'),
            order: 10,
            group: 'primary',
        },
        {
            ...metadata('action.settlement.instructions.correct', MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'FINANCIAL'),
            operationId: OPERATION_IDS.SETTLEMENT_INSTRUCTIONS_CORRECT,
            labelKey: tk('card.settlement.actions.correct'),
            iconKey: 'correct',
            kind: 'command',
            commandId: 'cmd.settlement.correct',
            intent: 'warning',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('card.settlement.saved'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('card.settlement.requestFailed'),
            order: 20,
            group: 'secondary',
        },
        {
            ...metadata('action.settlement.instructions.cancel', MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'FINANCIAL'),
            operationId: OPERATION_IDS.SETTLEMENT_INSTRUCTIONS_CANCEL,
            labelKey: tk('card.settlement.actions.cancel'),
            iconKey: 'cancel',
            kind: 'command',
            commandId: 'cmd.settlement.cancel',
            intent: 'danger',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('card.settlement.saved'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('card.settlement.requestFailed'),
            order: 30,
            group: 'danger',
        },
    ],
    pageActions: [
        {
            ...metadata('action.settlement.instructions.create', MODULE_KEYS.SETTLEMENT, FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS, 'FINANCIAL'),
            operationId: OPERATION_IDS.SETTLEMENT_INSTRUCTIONS_CREATE,
            labelKey: tk('card.settlement.actions.create'),
            iconKey: 'add',
            kind: 'command',
            commandId: 'cmd.settlement.create',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('card.settlement.saved'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('card.settlement.requestFailed'),
            order: 5,
            group: 'primary',
        },
    ],
};
export const CORPORATE_ACTION_TABLE = {
    ...metadata('table.data.corporateActions', MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'CONFIDENTIAL'),
    operationId: OPERATION_IDS.CORPORATE_ACTION_LIST,
    titleKey: tk('page.corporateActions.title'),
    dataSourceId: query('query.data.corporateActions.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'id', {
            sortable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'instrumentSymbol', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 140,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'actionType', {
            filterable: true,
            fieldType: 'select',
            enumOptions: [
                'DIVIDEND',
                'INTEREST',
                'BONUS',
                'RIGHTS_ISSUE',
                'SPLIT',
                'CONSOLIDATION',
                'MERGER',
                'SPINOFF',
            ].map((value) => ({
                value,
                labelKey: tk(`enum.corporateActionType.${value}`),
            })),
            requiredVisible: true,
            defaultWidth: 170,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'status', {
            filterable: true,
            fieldType: 'status',
            enumOptions: ['DRAFT', 'ANNOUNCED', 'PROCESSING', 'COMPLETED', 'CANCELLED'].map((value) => ({
                value,
                labelKey: tk(`enum.corporateActionStatus.${value}`),
            })),
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'exDate', {
            sortable: true,
            filterable: true,
            fieldType: 'date',
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'recordDate', {
            filterable: true,
            fieldType: 'date',
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'payDate', {
            filterable: true,
            fieldType: 'date',
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'declarationDate', {
            filterable: true,
            fieldType: 'date',
            defaultWidth: 140,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'fiscalYear', {
            filterable: true,
            defaultWidth: 110,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'ratioNumerator', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 100,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'ratioDenominator', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 110,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'cashRate', {
            fieldType: 'money',
            align: 'right',
            defaultWidth: 120,
            defaultVisible: false,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'exDate', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: true },
    createAction: lifecycleCreateAction('action.data.corporateActions.create', MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'CONFIDENTIAL', OPERATION_IDS.CORPORATE_ACTION_CREATE, ROUTE_IDS.DATA_CORPORATE_ACTION_CREATE),
};
CORPORATE_ACTION_TABLE.rowActions = [
    lifecycleEditAction('action.data.corporateActions.edit', CORPORATE_ACTION_TABLE, OPERATION_IDS.CORPORATE_ACTION_UPDATE, ROUTE_IDS.DATA_CORPORATE_ACTION_EDIT),
    {
        ...metadata('action.data.corporateActions.process', MODULE_KEYS.CORPORATE_ACTIONS, FEATURE_KEYS.DATA_CORPORATE_ACTIONS, 'FINANCIAL'),
        operationId: OPERATION_IDS.CORPORATE_ACTION_PROCESS,
        labelKey: tk('action.process'),
        iconKey: 'play',
        kind: 'command',
        method: 'POST',
        commandId: 'cmd.investment.corporateActions.process',
        intent: 'primary',
        idempotencyRequired: true,
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: tk('common:success'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common:error.unexpected'),
        visibilityCondition: { operator: 'in', field: 'status', values: ['DRAFT', 'ANNOUNCED'] },
        order: 20,
        group: 'primary',
    },
];
export const FACT_BATCH_TABLE = {
    ...metadata('table.data.factBatches', MODULE_KEYS.FACTS, FEATURE_KEYS.DATA_FACT_BATCHES, 'CONFIDENTIAL'),
    operationId: OPERATION_IDS.FACT_BATCHES_LIST,
    titleKey: tk('page.factBatches.title'),
    dataSourceId: query('query.data.factBatches.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.FACTS, FEATURE_KEYS.DATA_FACT_BATCHES, 'id', {
            sortable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.FACTS, FEATURE_KEYS.DATA_FACT_BATCHES, 'status', {
            filterable: true,
            fieldType: 'status',
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.FACTS, FEATURE_KEYS.DATA_FACT_BATCHES, 'createdAt', {
            sortable: true,
            fieldType: 'datetime',
            defaultWidth: 190,
        }),
        column(MODULE_KEYS.FACTS, FEATURE_KEYS.DATA_FACT_BATCHES, 'completedAt', {
            fieldType: 'datetime',
            defaultWidth: 190,
        }),
        column(MODULE_KEYS.FACTS, FEATURE_KEYS.DATA_FACT_BATCHES, 'recordCount', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 120,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'createdAt', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: false, create: true },
};
export const IMPORT_JOB_TABLE = {
    ...metadata('table.data.importJobs', MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'CONFIDENTIAL'),
    operationId: OPERATION_IDS.IMPORT_LIST,
    titleKey: tk('page.imports.title'),
    dataSourceId: query('query.data.importJobs.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'id', {
            sortable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'importType', {
            filterable: true,
            fieldType: 'select',
            enumOptions: [
                'INSTRUMENT',
                'TRANSACTION',
                'MARKET_DATA',
                'CORPORATE_ACTION',
                'CUSTODY_POSITION',
                'OPENING_CUTOVER',
                'TAX_RULE',
            ].map((value) => ({
                value,
                labelKey: tk(`enum.importType.${value}`),
            })),
            requiredVisible: true,
            defaultWidth: 170,
        }),
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'sourceFileName', {
            searchable: true,
            defaultWidth: 280,
            privacyClassification: 'CONFIDENTIAL',
        }),
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'status', {
            filterable: true,
            fieldType: 'status',
            enumOptions: [
                'PENDING',
                'PREVIEWING',
                'PREVIEWED',
                'COMMITTING',
                'COMMITTED',
                'FAILED',
                'PARTIAL',
                'RECOVERING',
            ].map((value) => ({
                value,
                labelKey: tk(`enum.importJobStatus.${value}`),
            })),
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'createdAt', {
            sortable: true,
            fieldType: 'datetime',
            defaultWidth: 190,
        }),
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'completedAt', {
            sortable: true,
            fieldType: 'datetime',
            defaultWidth: 190,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'recordCount', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 120,
        }),
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'errorCount', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.IMPORT, FEATURE_KEYS.DATA_IMPORTS, 'warningCount', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 120,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'createdAt', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: true },
};
export const RECONCILIATION_RUN_TABLE = {
    ...metadata('table.data.reconciliationRuns', MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'CONFIDENTIAL'),
    operationId: OPERATION_IDS.RECONCILIATION_LIST,
    titleKey: tk('page.reconciliation.title'),
    dataSourceId: query('query.data.reconciliationRuns.list'),
    rowIdField: 'runId',
    columns: [
        column(MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'runId', {
            sortable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'status', {
            filterable: true,
            fieldType: 'status',
            enumOptions: ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED'].map((value) => ({
                value,
                labelKey: tk(`enum.reconciliationStatus.${value}`),
            })),
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'startedAt', {
            sortable: true,
            fieldType: 'datetime',
            defaultWidth: 190,
        }),
        column(MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'completedAt', {
            fieldType: 'datetime',
            defaultWidth: 190,
        }),
        column(MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'totalRecords', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 120,
        }),
        column(MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'matchedRecords', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 140,
        }),
        column(MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'unmatchedRecords', {
            fieldType: 'number',
            align: 'right',
            defaultWidth: 150,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'startedAt', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: false, create: true },
    createAction: lifecycleCreateAction('action.data.reconciliation.create', MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'CONFIDENTIAL', OPERATION_IDS.RECONCILIATION_CREATE, ROUTE_IDS.DATA_RECONCILIATION_CREATE),
    rowActions: [
        {
            ...metadata('action.data.reconciliation.viewBreaks', MODULE_KEYS.RECONCILIATION, FEATURE_KEYS.DATA_RECONCILIATION, 'CONFIDENTIAL'),
            operationId: OPERATION_IDS.RECONCILIATION_RUN_BREAKS_LIST,
            labelKey: tk('action.viewBreaks'),
            iconKey: 'list',
            kind: 'navigate',
            intent: 'secondary',
            idempotencyRequired: false,
            successBehavior: 'NO_OP',
            errorBehavior: 'SHOW_INLINE_ERROR',
            targetRouteId: ROUTE_IDS.DATA_RECONCILIATION_DETAIL,
            order: 10,
            group: 'navigation',
        },
    ],
};
function adminLifecycleTable(id, titleKey, dataSourceId, fields) {
    return {
        ...metadata(id, MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION),
        titleKey: tk(titleKey),
        dataSourceId: query(dataSourceId),
        rowIdField: 'id',
        columns: fields.map((item) => column(MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION, item.field, {
            fieldType: item.fieldType,
            sortable: item.sortable ?? false,
            filterable: item.filterable ?? false,
            searchable: item.searchable ?? false,
            requiredVisible: item.requiredVisible ?? false,
            referenceDescriptor: item.referenceDescriptor,
            // P1-5: Hide persistence IDs by default — they are not primary labels.
            // Use requiredVisible: true to force-show an id column when needed.
            defaultVisible: item.defaultVisible ?? (item.field === 'id' ? false : item.requiredVisible !== false),
            defaultWidth: item.field === 'id' ? 140 : 180,
        })),
        pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
        operationMode: 'server',
        serverSidePagination: true,
        serverSideSorting: true,
        serverSideFiltering: true,
        capabilities: { ...standardCapabilities, create: true },
        toolbar: {
            showResultCount: true,
            showGlobalSearch: true,
            showFilterRowToggle: true,
            showColumnChooser: true,
            showRefresh: true,
            showCreateAction: true,
        },
    };
}
export const ORGANIZATION_BRANCHES_TABLE = adminLifecycleTable('table.admin.organizationBranches', 'page.branches.title', 'query.admin.organization-branches.list', [
    { field: 'id', sortable: true },
    { field: 'code', sortable: true, searchable: true, requiredVisible: true },
    { field: 'name', sortable: true, searchable: true, requiredVisible: true },
    { field: 'status', fieldType: 'status', filterable: true, requiredVisible: true },
    { field: 'isDefault', fieldType: 'boolean' },
    { field: 'parentBranchId', fieldType: 'text', requiredVisible: false },
    { field: 'parentBranchName', fieldType: 'text', searchable: true },
    { field: 'address', fieldType: 'text', searchable: true, defaultVisible: false },
    { field: 'createdAt', fieldType: 'datetime', sortable: true },
]);
ORGANIZATION_BRANCHES_TABLE.rowActions = [
    lifecycleEditAction('action.organizationBranch.edit', ORGANIZATION_BRANCHES_TABLE, OPERATION_IDS.ORGANIZATION_BRANCHES_UPDATE, ROUTE_IDS.ORGANIZATION_BRANCHES_EDIT),
    lifecycleDeleteAction('action.organizationBranch.delete', ORGANIZATION_BRANCHES_TABLE, OPERATION_IDS.ORGANIZATION_BRANCHES_DISABLE, 'cmd.admin.organization-branches.delete'),
];
ORGANIZATION_BRANCHES_TABLE.rowActions[1] = {
    ...ORGANIZATION_BRANCHES_TABLE.rowActions[1],
    confirmation: {
        ...ORGANIZATION_BRANCHES_TABLE.rowActions[1].confirmation,
        fields: [
            {
                name: 'reason',
                labelKey: 'field.branch.disableReason',
                fieldType: 'text',
                required: true,
            },
        ],
    },
};
export const MODULE_ASSIGNMENTS_TABLE = adminLifecycleTable('table.admin.organizationModuleAssignments', 'page.moduleAssignments.title', 'query.admin.organization-module-activations.list', [
    { field: 'id', sortable: true },
    { field: 'module', sortable: true, searchable: true, requiredVisible: true },
    { field: 'status', fieldType: 'status', filterable: true, requiredVisible: true },
    { field: 'configurationVersionId', searchable: true },
    { field: 'version', fieldType: 'number', sortable: true },
]);
Object.assign(MODULE_ASSIGNMENTS_TABLE, {
    rowIdField: 'module',
    rowActivationAction: {
        ...metadata('action.moduleAssignment.configure', MODULE_KEYS.ADMIN, FEATURE_KEYS.ADMIN_ORGANIZATION),
        operationId: OPERATION_IDS.ORGANIZATION_MODULES_CONFIGURE,
        labelKey: tk('action.edit'),
        kind: 'navigate',
        intent: 'neutral',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: ROUTE_IDS.ORGANIZATION_MODULES_CONFIGURE,
        // Row-action templates bind against response field names. The canonical
        // route names this segment moduleId, while assignment rows expose module.
        successNavigateTo: '/admin/modules/:module/configure',
        errorBehavior: 'SHOW_TOAST_ERROR',
        group: 'navigation',
    },
    rowActions: [
        lifecycleDeleteAction('action.organizationModuleAssignment.delete', MODULE_ASSIGNMENTS_TABLE, OPERATION_IDS.ORGANIZATION_MODULES_DISABLE, 'cmd.admin.organization-module-activations.delete'),
    ],
});
export const ORGANIZATION_CLIENTS_TABLE = adminLifecycleTable('table.admin.organizationClients', 'page.clients.title', 'query.admin.organization-clients.list', [
    { field: 'id', sortable: true },
    { field: 'code', sortable: true, searchable: true, requiredVisible: true },
    { field: 'fullName', sortable: true, searchable: true, requiredVisible: true },
    { field: 'accountType', filterable: true },
    { field: 'email', searchable: true },
    { field: 'phone', searchable: true, defaultVisible: false },
    { field: 'panNumber', searchable: true, defaultVisible: false },
    { field: 'isActive', fieldType: 'boolean', filterable: true },
]);
ORGANIZATION_CLIENTS_TABLE.rowActions = [
    lifecycleEditAction('action.organizationClient.edit', ORGANIZATION_CLIENTS_TABLE, OPERATION_IDS.ORGANIZATION_CLIENTS_UPDATE, ROUTE_IDS.ORGANIZATION_CLIENTS_EDIT),
    lifecycleDeleteAction('action.organizationClient.delete', ORGANIZATION_CLIENTS_TABLE, OPERATION_IDS.ORGANIZATION_CLIENTS_DISABLE, 'cmd.admin.organization-clients.delete'),
];
export const ORGANIZATION_CLIENT_ACCOUNTS_TABLE = adminLifecycleTable('table.admin.organizationClientAccounts', 'page.clientAccounts.title', 'query.admin.organization-client-accounts.list', [
    { field: 'id', sortable: true },
    {
        field: 'client',
        fieldType: 'reference',
        filterable: true,
        requiredVisible: true,
        referenceDescriptor: {
            entityType: 'client-account',
            idField: 'id',
            labelField: 'label',
            secondaryLabelField: 'secondaryLabel',
            resolutionMode: 'embedded',
            idDisplayPolicy: 'tooltip',
        },
    },
    { field: 'accountNumber', sortable: true, searchable: true, requiredVisible: true },
    { field: 'baseCurrency', filterable: true, defaultVisible: false },
    { field: 'accountType', filterable: true, requiredVisible: true },
    { field: 'currentBalance', fieldType: 'money', sortable: true, requiredVisible: true },
    { field: 'isActive', fieldType: 'boolean', filterable: true },
]);
ORGANIZATION_CLIENT_ACCOUNTS_TABLE.rowActions = [
    lifecycleEditAction('action.organizationClientAccount.edit', ORGANIZATION_CLIENT_ACCOUNTS_TABLE, OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_UPDATE, ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_EDIT),
    lifecycleDeleteAction('action.organizationClientAccount.disable', ORGANIZATION_CLIENT_ACCOUNTS_TABLE, OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_DISABLE, 'cmd.admin.organization-client-accounts.delete'),
];
export const ORGANIZATION_PORTFOLIOS_TABLE = adminLifecycleTable('table.admin.organizationPortfolios', 'page.portfolios.title', 'query.admin.organization-portfolios.list', [
    { field: 'id', sortable: true },
    { field: 'name', sortable: true, searchable: true, requiredVisible: true },
    {
        field: 'client',
        fieldType: 'reference',
        filterable: true,
        requiredVisible: true,
        referenceDescriptor: {
            entityType: 'client-account',
            idField: 'id',
            labelField: 'label',
            resolutionMode: 'embedded',
            idDisplayPolicy: 'tooltip',
        },
    },
    {
        field: 'custodyAccount',
        fieldType: 'reference',
        filterable: true,
        referenceDescriptor: {
            entityType: 'portfolio-account',
            idField: 'id',
            labelField: 'label',
            resolutionMode: 'embedded',
            idDisplayPolicy: 'tooltip',
        },
    },
    { field: 'baseCurrency', filterable: true, defaultVisible: false },
    { field: 'strategy', searchable: true },
    { field: 'status', filterable: true, requiredVisible: true },
    { field: 'mandateStartDate', fieldType: 'date', sortable: true, defaultVisible: false },
    { field: 'mandateEndDate', fieldType: 'date', sortable: true, defaultVisible: false },
]);
ORGANIZATION_PORTFOLIOS_TABLE.rowActions = [
    lifecycleEditAction('action.organizationPortfolio.edit', ORGANIZATION_PORTFOLIOS_TABLE, OPERATION_IDS.ORGANIZATION_PORTFOLIOS_UPDATE, ROUTE_IDS.ORGANIZATION_PORTFOLIOS_EDIT),
    lifecycleDeleteAction('action.organizationPortfolio.disable', ORGANIZATION_PORTFOLIOS_TABLE, OPERATION_IDS.ORGANIZATION_PORTFOLIOS_DISABLE, 'cmd.admin.organization-portfolios.delete'),
];
export const LOTS_TABLE = {
    ...metadata('table.data.holdings', MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'FINANCIAL'),
    operationId: OPERATION_IDS.HOLDINGS_VIEW,
    titleKey: tk('page.holdings.title'),
    dataSourceId: query('query.data.holdings.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'instrumentSymbol', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
        }),
        // P1-5: persistence IDs hidden by default — use instrumentSymbol as primary label
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'clientAccountId', {
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'portfolioAccountId', {
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'totalQuantity', {
            fieldType: 'quantity',
            sortable: true,
            requiredVisible: true,
            align: 'right',
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'availableQuantity', {
            fieldType: 'quantity',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'totalCost', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'weightedAverageCost', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'marketValue', {
            fieldType: 'money',
            align: 'right',
        }),
        // P1-6: Market-derived value provenance — currency, source, freshness
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'marketValueCurrency', {
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'valuationSource', {
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'valuationFreshness', {
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'unrealizedGainLoss', {
            fieldType: 'money',
            align: 'right',
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'valuationAsOf', {
            fieldType: 'date',
            filterable: true,
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'lastUpdated', {
            fieldType: 'datetime',
            sortable: true,
        }),
        // Detail-only, backend-projected fields. They remain hidden in the list
        // because valuation and lot provenance must never be reconstructed in the
        // browser from aggregate values.
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'valuation', {
            defaultVisible: false,
        }),
        column(MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'lots', {
            defaultVisible: false,
        }),
    ],
    rowActivationAction: {
        ...metadata('action.holdings.view', MODULE_KEYS.HOLDINGS, FEATURE_KEYS.DATA_HOLDINGS, 'FINANCIAL'),
        operationId: OPERATION_IDS.HOLDINGS_VIEW,
        labelKey: tk('action.view'),
        kind: 'navigate',
        intent: 'neutral',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: ROUTE_IDS.DATA_HOLDINGS_DETAIL,
        successNavigateTo: routePath(ROUTE_IDS.DATA_HOLDINGS_DETAIL),
        errorBehavior: 'SHOW_TOAST_ERROR',
        group: 'navigation',
    },
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'lastUpdated', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: true, create: false },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showColumnChooser: true,
        showRefresh: true,
        showExport: true,
    },
};
export const DATA_CLIENTS_TABLE = {
    ...metadata('table.data.clients', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'FINANCIAL'),
    operationId: OPERATION_IDS.DATA_CLIENTS_LIST,
    titleKey: tk('card.clientsData.title'),
    dataSourceId: query('query.data.clients.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'id', {
            sortable: true,
            searchable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'displayName', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'email', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'phone', {
            searchable: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'panNumber', {
            searchable: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'clientType', {
            filterable: true,
            fieldType: 'select',
            defaultWidth: 140,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'isActive', {
            filterable: true,
            fieldType: 'status',
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CLIENTS, 'createdAt', {
            sortable: true,
            filterable: true,
            fieldType: 'date',
            defaultWidth: 140,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    defaultSort: { field: 'displayName', direction: 'asc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: true },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
        showExport: true,
    },
};
export const DATA_PORTFOLIOS_TABLE = {
    ...metadata('table.data.portfolios', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'FINANCIAL'),
    operationId: OPERATION_IDS.DATA_PORTFOLIOS_LIST,
    titleKey: tk('card.portfoliosData.title'),
    dataSourceId: query('query.data.portfolios.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'id', {
            sortable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'accountNumber', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'ownerClientAccountId', {
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'ownerType', {
            filterable: true,
            fieldType: 'select',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'boId', {
            sortable: true,
            searchable: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'dpCode', {
            sortable: true,
            filterable: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'strategy', {
            filterable: true,
            defaultWidth: 140,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'baseCurrency', {
            filterable: true,
            defaultWidth: 120,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'mandateStartDate', {
            fieldType: 'date',
            sortable: true,
            defaultWidth: 140,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'mandateEndDate', {
            fieldType: 'date',
            sortable: true,
            defaultWidth: 140,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'isDefault', {
            fieldType: 'boolean',
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'isActive', {
            filterable: true,
            fieldType: 'status',
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PORTFOLIOS, 'createdAt', {
            sortable: true,
            filterable: true,
            fieldType: 'date',
            defaultWidth: 140,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    defaultSort: { field: 'accountNumber', direction: 'asc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: true, create: true },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
    },
};
export const DATA_POSITIONS_TABLE = {
    ...metadata('table.data.positions', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'FINANCIAL'),
    operationId: OPERATION_IDS.DATA_POSITIONS_LIST,
    titleKey: tk('card.positions.title'),
    descriptionKey: tk('card.positions.description'),
    dataSourceId: query('query.data.positions.list'),
    rowIdField: 'id',
    // Positions are a read-only projection of the same PortfolioHolding
    // aggregates that back LOTS_TABLE. Column names are aligned with the
    // holding model so that the two surfaces present consistent semantics:
    // "positions" is the portfolio-management view; "lots" is the
    // detailed custody/valuation view with lot provenance.
    columns: [
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'instrumentSymbol', {
            sortable: true,
            filterable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'portfolioAccountId', {
            sortable: true,
            filterable: true,
            searchable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'portfolioAccountNumber', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'totalQuantity', {
            fieldType: 'quantity',
            sortable: true,
            requiredVisible: true,
            align: 'right',
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'availableQuantity', {
            fieldType: 'quantity',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'totalCost', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'weightedAverageCost', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'marketValue', {
            fieldType: 'money',
            sortable: true,
            requiredVisible: true,
            align: 'right',
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'marketValueCurrency', {
            filterable: true,
            defaultVisible: false,
            defaultWidth: 100,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'unrealizedGainLoss', {
            fieldType: 'money',
            sortable: true,
            requiredVisible: true,
            align: 'right',
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'valuationAsOf', {
            fieldType: 'date',
            sortable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'valuationSource', {
            filterable: true,
            defaultWidth: 120,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'valuationFreshness', {
            fieldType: 'status',
            defaultWidth: 120,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'lastUpdated', {
            fieldType: 'datetime',
            sortable: true,
            defaultVisible: false,
            defaultWidth: 130,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'marketValue', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: true, create: false },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
        showExport: true,
    },
    rowActions: [
        {
            ...metadata('action.data.positions.transfer', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'CONFIDENTIAL'),
            operationId: OPERATION_IDS.POSITION_TRANSFERS_REQUEST,
            labelKey: tk('card.positionTransfers.actions.request'),
            iconKey: 'swap',
            kind: 'navigate',
            intent: 'secondary',
            idempotencyRequired: false,
            successBehavior: 'NAVIGATE_TO',
            errorBehavior: 'SHOW_INLINE_ERROR',
            targetRouteId: ROUTE_IDS.DATA_POSITION_TRANSFERS,
            successNavigateTo: routePath(ROUTE_IDS.DATA_POSITION_TRANSFERS),
            order: 10,
            group: 'secondary',
        },
    ],
    rowActivationAction: {
        ...metadata('action.data.positions.view', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_POSITIONS, 'CONFIDENTIAL'),
        operationId: OPERATION_IDS.DATA_POSITIONS_GET,
        labelKey: tk('action.view'),
        iconKey: 'view',
        kind: 'navigate',
        intent: 'neutral',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: ROUTE_IDS.DATA_POSITIONS_DETAIL,
        successNavigateTo: routePath(ROUTE_IDS.DATA_POSITIONS_DETAIL, 'id'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        order: 5,
        group: 'navigation',
    },
};
export const CASH_POSITIONS_TABLE = {
    ...metadata('table.data.cashPositions', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'FINANCIAL'),
    operationId: OPERATION_IDS.DATA_CASH_LIST,
    titleKey: tk('card.cash.title'),
    dataSourceId: query('query.data.cashPositions.list'),
    rowIdField: 'id',
    columns: [
        // P1-13: Replace raw client/portfolio IDs with embedded business references.
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'clientAccountId', {
            sortable: true,
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'clientCode', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'clientName', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 220,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'portfolioAccountId', {
            sortable: true,
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'portfolioAccountNumber', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'currency', {
            sortable: true,
            filterable: true,
            searchable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'currentBalance', {
            fieldType: 'money',
            sortable: true,
            requiredVisible: true,
            align: 'right',
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'asOfDate', {
            fieldType: 'date',
            sortable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'sourceBatchId', {
            searchable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'sourceState', {
            fieldType: 'status',
            defaultWidth: 120,
        }),
        column(MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'updatedAt', {
            fieldType: 'datetime',
            sortable: true,
            defaultVisible: false,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'updatedAt', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: true, create: false },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
        showExport: true,
    },
    rowActivationAction: {
        ...metadata('action.data.cash.view', MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_CASH, 'FINANCIAL'),
        operationId: OPERATION_IDS.DATA_CASH_GET,
        labelKey: tk('action.view'),
        iconKey: 'view',
        kind: 'navigate',
        intent: 'neutral',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: ROUTE_IDS.DATA_CASH_DETAIL,
        successNavigateTo: routePath(ROUTE_IDS.DATA_CASH_DETAIL, 'id'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        order: 5,
        group: 'navigation',
    },
};
export const DATA_JOURNALS_TABLE = {
    ...metadata('table.data.journals', MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'FINANCIAL'),
    operationId: OPERATION_IDS.JOURNALS_LIST,
    titleKey: tk('card.journals.title'),
    dataSourceId: query('query.data.journals.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'id', {
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'entryNumber', {
            requiredVisible: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'reference', {
            requiredVisible: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'narration', {
            requiredVisible: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'status', {
            filterable: true,
            fieldType: 'status',
            requiredVisible: true,
            defaultWidth: 120,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'entryDate', {
            fieldType: 'date',
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'fiscalYearId', {
            filterable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'postedAt', {
            fieldType: 'datetime',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'reversalOf', {
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'totalDebit', {
            fieldType: 'money',
            align: 'right',
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'totalCredit', {
            fieldType: 'money',
            align: 'right',
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'createdAt', {
            fieldType: 'datetime',
            defaultWidth: 150,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'version', {
            fieldType: 'number',
            defaultVisible: false,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: false,
    serverSideFiltering: true,
    capabilities: {
        ...standardCapabilities,
        globalSearch: false,
        create: true,
        export: false,
        sorting: false,
    },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: false,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
        showExport: false,
        showCreateAction: true,
    },
};
DATA_JOURNALS_TABLE.rowActions = [
    {
        ...metadata('action.accounting.journals.post', MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'FINANCIAL'),
        operationId: ACCOUNTING_JOURNAL_POST,
        labelKey: tk('action.post'),
        iconKey: 'post',
        kind: 'command',
        commandId: 'cmd.data.journals.post',
        intent: 'primary',
        idempotencyRequired: true,
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: tk('common.success'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        visibilityCondition: {
            operator: 'and',
            conditions: [
                { operator: 'equals', field: 'status', value: 'DRAFT' },
                { operator: 'permissionAvailable', permission: 'organization:accounting:manage' },
            ],
        },
        order: 10,
        group: 'primary',
    },
    {
        ...metadata('action.accounting.journals.reverse', MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_JOURNALS, 'FINANCIAL'),
        operationId: ACCOUNTING_JOURNAL_REVERSE,
        labelKey: tk('action.reverse'),
        iconKey: 'reverse',
        kind: 'command',
        commandId: 'cmd.data.journals.reverse',
        intent: 'warning',
        idempotencyRequired: true,
        confirmation: {
            titleKey: tk('journal.reverseTitle'),
            messageKey: tk('journal.reversePrompt'),
            confirmLabelKey: tk('action.reverse'),
            cancelLabelKey: tk('action.cancel'),
            fields: [
                {
                    name: 'reversalDate',
                    labelKey: tk('journal.reversalDate'),
                    fieldType: 'text',
                    required: true,
                },
                {
                    name: 'reason',
                    labelKey: tk('journal.reversalReason'),
                    fieldType: 'text',
                    required: true,
                },
            ],
        },
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: tk('common.success'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        visibilityCondition: {
            operator: 'and',
            conditions: [
                { operator: 'equals', field: 'status', value: 'POSTED' },
                { operator: 'permissionAvailable', permission: 'organization:accounting:manage' },
            ],
        },
        order: 20,
        group: 'danger',
    },
];
export const DATA_FISCAL_PERIODS_TABLE = {
    ...metadata('table.data.fiscalPeriods', MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_FISCAL_PERIODS, 'FINANCIAL'),
    operationId: OPERATION_IDS.FISCAL_PERIODS_LIST,
    titleKey: tk('card.fiscalPeriods.title'),
    dataSourceId: query('query.data.fiscalPeriods.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_FISCAL_PERIODS, 'name', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_FISCAL_PERIODS, 'fiscalYearName', {
            filterable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_FISCAL_PERIODS, 'startDate', {
            fieldType: 'date',
            sortable: true,
            filterable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_FISCAL_PERIODS, 'endDate', {
            fieldType: 'date',
            sortable: true,
            filterable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_FISCAL_PERIODS, 'isLocked', {
            filterable: true,
            fieldType: 'boolean',
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.DATA_FISCAL_PERIODS, 'periodNumber', {
            sortable: true,
            align: 'right',
            defaultWidth: 80,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'startDate', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: false, create: true },
    toolbar: {
        showResultCount: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
        showCreateAction: true,
    },
};
export const DATA_FISCAL_YEARS_TABLE = {
    ...metadata('table.data.fiscalYears', MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_FISCAL_CALENDAR, 'FINANCIAL'),
    operationId: OPERATION_IDS.ACCOUNTING_FISCAL_YEAR_LIST,
    titleKey: tk('card.fiscalYears.title'),
    dataSourceId: query('query.admin.fiscalYears.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_FISCAL_CALENDAR, 'name', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_FISCAL_CALENDAR, 'yearCode', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_FISCAL_CALENDAR, 'startDate', {
            fieldType: 'date',
            sortable: true,
            filterable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_FISCAL_CALENDAR, 'endDate', {
            fieldType: 'date',
            sortable: true,
            filterable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_FISCAL_CALENDAR, 'status', {
            filterable: true,
            defaultWidth: 110,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_FISCAL_CALENDAR, 'isClosed', {
            filterable: true,
            fieldType: 'boolean',
            defaultWidth: 110,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'startDate', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, globalSearch: false, create: true },
    toolbar: {
        showResultCount: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
        showCreateAction: true,
    },
};
export const DASHBOARD_HOLDINGS_TABLE = {
    ...metadata('table.dashboard.holdings', MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, 'FINANCIAL'),
    operationId: OPERATION_IDS.DASHBOARD_VIEW,
    titleKey: tk('dashboard.widgets.holdings'),
    dataSourceId: query('query.dashboard.holdings'),
    rowIdField: 'symbol',
    columns: [
        column(MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, 'symbol', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, 'quantity', {
            fieldType: 'quantity',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, 'avgCost', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, 'marketPrice', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, 'marketValue', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, 'unrealizedPnl', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, 'change', {
            sortable: true,
            align: 'right',
        }),
    ],
    pagination: { pageSize: 10, pageSizeOptions: [5, 10, 25] },
    defaultSort: { field: 'marketValue', direction: 'desc' },
    operationMode: 'client',
    serverSidePagination: false,
    serverSideSorting: false,
    serverSideFiltering: false,
    capabilities: { ...standardCapabilities, create: false, export: false },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showColumnChooser: false,
        showRefresh: false,
        showExport: false,
    },
};
export const DATA_TAX_RECORDS_TABLE = {
    ...metadata('table.data.taxRecords', MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'FINANCIAL'),
    operationId: OPERATION_IDS.TAX_RECORDS_LIST,
    titleKey: tk('card.taxRecords.title'),
    dataSourceId: query('query.data.taxRecords.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'instrumentSymbol', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'clientAccountId', { filterable: true }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'saleDate', {
            fieldType: 'date',
            sortable: true,
            filterable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'capitalGain', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
            requiredVisible: true,
        }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'taxAmount', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
            requiredVisible: true,
        }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'taxRate', {
            fieldType: 'percentage',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'holdingPeriodDays', {
            fieldType: 'number',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'taxDomain', { filterable: true }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'ruleVersion', { defaultWidth: 90 }),
        column(MODULE_KEYS.TAX, FEATURE_KEYS.DATA_TAX_RECORDS, 'sourceAuthority', {
            defaultWidth: 110,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'saleDate', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: false },
    toolbar: {
        showResultCount: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
    },
};
export const DATA_RISK_EXPOSURES_TABLE = {
    ...metadata('table.data.riskExposures', MODULE_KEYS.RISK, FEATURE_KEYS.DATA_RISK_EXPOSURES, 'FINANCIAL'),
    operationId: OPERATION_IDS.RISK_EXPOSURES_LIST,
    titleKey: tk('card.riskExposures.title'),
    dataSourceId: query('query.data.riskExposures.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.RISK, FEATURE_KEYS.DATA_RISK_EXPOSURES, 'instrumentSymbol', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.RISK, FEATURE_KEYS.DATA_RISK_EXPOSURES, 'portfolioAccountId', {
            filterable: true,
        }),
        column(MODULE_KEYS.RISK, FEATURE_KEYS.DATA_RISK_EXPOSURES, 'quantity', {
            fieldType: 'quantity',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.RISK, FEATURE_KEYS.DATA_RISK_EXPOSURES, 'totalCost', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
            requiredVisible: true,
        }),
        column(MODULE_KEYS.RISK, FEATURE_KEYS.DATA_RISK_EXPOSURES, 'concentrationPct', {
            fieldType: 'percentage',
            sortable: true,
            align: 'right',
            requiredVisible: true,
        }),
        column(MODULE_KEYS.RISK, FEATURE_KEYS.DATA_RISK_EXPOSURES, 'riskMeasure', { filterable: true }),
        column(MODULE_KEYS.RISK, FEATURE_KEYS.DATA_RISK_EXPOSURES, 'asOfDate', {
            fieldType: 'date',
            sortable: true,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'concentrationPct', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: false },
    toolbar: {
        showResultCount: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
    },
};
export const DATA_COMPLIANCE_BREACHES_TABLE = {
    ...metadata('table.data.complianceBreaches', MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'FINANCIAL'),
    operationId: OPERATION_IDS.COMPLIANCE_BREACHES_LIST,
    titleKey: tk('card.complianceBreaches.title'),
    dataSourceId: query('query.data.complianceBreaches.list'),
    // Breaches are lifecycle cases, not position rows: several cases can refer
    // to one instrument, so the persisted breach identity is the only stable
    // table key.
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'breachType', {
            sortable: true,
            filterable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'severity', {
            sortable: true,
            filterable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'instrumentSymbol', {
            sortable: true,
            searchable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'portfolioAccountId', {
            filterable: true,
        }),
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'breachValue', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
            requiredVisible: true,
        }),
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'limitValue', {
            fieldType: 'money',
            align: 'right',
        }),
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'status', {
            sortable: true,
            filterable: true,
        }),
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'detectedAt', {
            fieldType: 'date',
            sortable: true,
        }),
        column(MODULE_KEYS.COMPLIANCE, FEATURE_KEYS.DATA_COMPLIANCE_BREACHES, 'ruleId', {
            searchable: true,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'detectedAt', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: false },
    toolbar: {
        showResultCount: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
    },
};
/**
 * Organization-scoped investment transaction worklist.  This descriptor is
 * consumed by both the authenticated experience resolver and the bespoke
 * transaction route; keeping it here prevents the browser from advertising a
 * table shape that the backend cannot authorize or execute.
 */
export const TRANSACTION_TABLE = {
    ...metadata('table.investment.transactions', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
    operationId: OPERATION_IDS.TRANSACTION_LIST,
    titleKey: tk('page.transactions.title'),
    dataSourceId: query('query.investment.transactions.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'id', {
            sortable: true,
            defaultVisible: false,
            defaultWidth: 120,
            privacyClassification: 'CONFIDENTIAL',
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'transactionDate', {
            fieldType: 'date',
            sortable: true,
            filterable: true,
            allowedFilterOperators: ['equals'],
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'transactionType', {
            fieldType: 'select',
            filterable: true,
            allowedFilterOperators: ['equals'],
            groupable: true,
            defaultWidth: 150,
            enumOptions: [
                'PURCHASE',
                'SALE',
                'BONUS_SHARE',
                'RIGHT_SHARE',
                'TRANSFER_IN',
                'TRANSFER_OUT',
                'FEE',
                'TAX',
            ].map((value) => ({ value, labelKey: tk(`enum.txnType.${value}`) })),
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'instrumentSymbol', {
            sortable: true,
            filterable: true,
            searchable: true,
            allowedFilterOperators: ['equals'],
            groupable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'currency', {
            filterable: true,
            allowedFilterOperators: ['equals'],
            defaultWidth: 90,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'clientAccountId', {
            filterable: true,
            allowedFilterOperators: ['equals'],
            defaultVisible: false,
            privacyClassification: 'CONFIDENTIAL',
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'portfolioAccountId', {
            filterable: true,
            allowedFilterOperators: ['equals'],
            defaultVisible: false,
            privacyClassification: 'CONFIDENTIAL',
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'quantity', {
            fieldType: 'quantity',
            sortable: true,
            align: 'right',
            defaultWidth: 120,
            allowedAggregations: ['sum'],
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'pricePerUnit', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
            defaultWidth: 130,
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'totalAmount', {
            fieldType: 'money',
            sortable: true,
            align: 'right',
            defaultWidth: 140,
            allowedAggregations: ['sum'],
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'commission', {
            fieldType: 'money',
            align: 'right',
            defaultWidth: 120,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'sebonFee', {
            fieldType: 'money',
            align: 'right',
            defaultWidth: 120,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'dpCharges', {
            fieldType: 'money',
            align: 'right',
            defaultWidth: 120,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'status', {
            fieldType: 'status',
            filterable: true,
            allowedFilterOperators: ['equals'],
            groupable: true,
            defaultWidth: 155,
            enumOptions: [
                'DRAFT',
                'SUBMITTED',
                'PENDING_APPROVAL',
                'APPROVED',
                'REJECTED',
                'WITHDRAWN',
                'POSTED',
                'REVERSED',
                'ROLLED_BACK',
            ].map((value) => ({ value, labelKey: tk(`enum.txnStatus.${value}`) })),
        }),
        column(MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'recoveryQueue', {
            fieldType: 'boolean',
            filterable: true,
            allowedFilterOperators: ['equals'],
            defaultVisible: false,
            hideable: false,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    defaultSort: { field: 'transactionDate', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    serverSideGrouping: true,
    serverSideAggregation: true,
    export: {
        formats: ['csv'],
        defaultFormat: 'csv',
        scopes: ['all'],
        defaultScope: 'all',
        formulaInjectionProtection: true,
        serverExport: {
            operationId: OPERATION_IDS.TRANSACTION_EXPORT,
            endpoint: '/api/transactions/export',
            method: 'POST',
            filterParameterMap: {
                status: 'status',
                transactionType: 'type',
                transactionDate: 'transactionDate',
                portfolioAccountId: 'portfolioAccountId',
                instrumentSymbol: 'instrumentSymbol',
                currency: 'currency',
            },
            searchParameter: 'search',
        },
    },
    privacyAndExportPolicy: {
        allowedExportFormats: ['csv'],
        auditExports: true,
    },
    selectionMode: 'multiple',
    rowActions: [
        {
            ...metadata('action.investment.transactions.submit', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
            operationId: TRANSACTION_SUBMIT,
            labelKey: tk('action.submit'),
            iconKey: 'submit',
            kind: 'command',
            commandId: 'cmd.investment.transactions.submit',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            // P0-2: Status eligibility is server-authoritative via row.actionEligibility.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:transactions:submit',
            },
            order: 10,
            group: 'primary',
        },
        {
            ...metadata('action.investment.transactions.approve', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
            operationId: OPERATION_IDS.TRANSACTION_APPROVE,
            labelKey: tk('action.approve'),
            iconKey: 'approve',
            kind: 'command',
            commandId: 'cmd.investment.transactions.approve',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            // P0-2: Status + maker-checker eligibility is server-authoritative via
            // row.actionEligibility. Permission is checked by the backend resolver.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:transactions:approve',
            },
            order: 20,
            group: 'primary',
        },
        {
            ...metadata('action.investment.transactions.reject', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
            operationId: TRANSACTION_REJECT,
            labelKey: tk('approvals:actions.reject'),
            iconKey: 'cancel',
            kind: 'command',
            commandId: 'cmd.investment.transactions.reject',
            intent: 'warning',
            idempotencyRequired: true,
            confirmation: {
                titleKey: tk('transaction:approvals.title'),
                messageKey: tk('common:confirm'),
                confirmLabelKey: tk('approvals:actions.reject'),
                cancelLabelKey: tk('action.cancel'),
                fields: [
                    {
                        name: 'reason',
                        labelKey: tk('transaction:reject.reason'),
                        fieldType: 'text',
                        required: true,
                    },
                ],
            },
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            // P0-2: Status + maker-checker eligibility is server-authoritative via
            // row.actionEligibility. Permission is checked by the backend resolver.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:transactions:reject',
            },
            order: 25,
            group: 'danger',
        },
        {
            ...metadata('action.investment.transactions.post', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
            operationId: TRANSACTION_POST,
            labelKey: tk('action.post'),
            iconKey: 'post',
            kind: 'command',
            commandId: 'cmd.investment.transactions.post',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            // P0-2: Status + posting eligibility is server-authoritative via row.actionEligibility.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:transactions:post',
            },
            order: 30,
            group: 'primary',
        },
        {
            ...metadata('action.investment.transactions.withdraw', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
            operationId: TRANSACTION_WITHDRAW,
            labelKey: tk('action.withdraw'),
            iconKey: 'withdraw',
            kind: 'command',
            commandId: 'cmd.investment.transactions.withdraw',
            intent: 'warning',
            idempotencyRequired: true,
            confirmation: {
                titleKey: tk('action.withdraw'),
                messageKey: tk('action.withdraw'),
                confirmLabelKey: tk('action.withdraw'),
                cancelLabelKey: tk('action.cancel'),
                fields: [
                    {
                        name: 'reason',
                        labelKey: tk('transaction:reverse.reasonLabel'),
                        fieldType: 'text',
                        required: true,
                    },
                ],
            },
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            // P0-2: Status eligibility is server-authoritative via row.actionEligibility.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:transactions:withdraw',
            },
            order: 40,
            group: 'danger',
        },
        {
            ...metadata('action.investment.transactions.reverse', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
            operationId: OPERATION_IDS.TRANSACTION_REVERSE,
            labelKey: tk('action.reverse'),
            iconKey: 'reverse',
            kind: 'command',
            commandId: 'cmd.investment.transactions.reverse',
            intent: 'warning',
            idempotencyRequired: true,
            confirmation: {
                titleKey: tk('transaction:reverse.title'),
                messageKey: tk('transaction:reverse.reasonPrompt'),
                confirmLabelKey: tk('transaction:reverse.confirm'),
                cancelLabelKey: tk('action.cancel'),
                fields: [
                    {
                        name: 'reason',
                        labelKey: tk('transaction:reverse.reasonLabel'),
                        fieldType: 'text',
                        required: true,
                    },
                ],
            },
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            // P0-2: Reversal eligibility is server-authoritative via row.actionEligibility.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:transactions:reverse',
            },
            order: 50,
            group: 'danger',
        },
        {
            ...metadata('action.investment.transactions.repair', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
            operationId: TRANSACTION_REPAIR,
            labelKey: tk('action.repair'),
            iconKey: 'warning',
            kind: 'command',
            commandId: 'cmd.investment.transactions.repair',
            intent: 'warning',
            idempotencyRequired: true,
            confirmation: {
                titleKey: tk('transaction:reversals.title'),
                messageKey: tk('common:confirm'),
                confirmLabelKey: tk('action.repair'),
                cancelLabelKey: tk('action.cancel'),
                fields: [
                    {
                        name: 'reason',
                        labelKey: tk('transaction:reject.reason'),
                        fieldType: 'text',
                        required: true,
                    },
                ],
            },
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            // P0-2: Repair eligibility is server-authoritative via row.actionEligibility.
            visibilityCondition: {
                operator: 'permissionAvailable',
                permission: 'organization:transactions:repair',
            },
            order: 60,
            group: 'danger',
        },
        {
            ...metadata('action.investment.transactions.repost', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL'),
            operationId: TRANSACTION_REPOST,
            labelKey: tk('action.repost'),
            iconKey: 'post',
            kind: 'command',
            commandId: 'cmd.investment.transactions.repost',
            intent: 'primary',
            idempotencyRequired: true,
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            visibilityCondition: {
                operator: 'and',
                conditions: [
                    { operator: 'equals', field: 'repostEligible', value: true },
                    { operator: 'permissionAvailable', permission: 'organization:transactions:repost' },
                ],
            },
            order: 70,
            group: 'primary',
        },
    ],
    pageActions: [
        importPageAction('action.investment.transactions.import', MODULE_KEYS.TRANSACTIONS, FEATURE_KEYS.TRANSACTIONS_LIST, 'FINANCIAL', 'TRANSACTION'),
    ],
    capabilities: {
        ...standardCapabilities,
        grouping: true,
        aggregation: true,
        selection: true,
        savedViews: true,
        create: true,
    },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
        showSavedViews: true,
    },
    transfer: {
        import: {
            definitionId: 'TRANSACTION',
            autoDetection: true,
            templatesSupported: true,
        },
        export: {
            formats: ['csv', 'xlsx'],
            scopes: ['all', 'filtered', 'selected'],
            presetsSupported: true,
            asyncThreshold: 10_000,
        },
    },
};
export const OPENING_CUTOVER_TABLE = {
    ...metadata('table.admin.openingCutover', MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'CONFIDENTIAL'),
    titleKey: tk('page.openingCutover.title'),
    dataSourceId: query('query.admin.organization-opening-cutover.list'),
    rowIdField: 'id',
    columns: [
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'id', {
            defaultVisible: false,
            defaultWidth: 120,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'asOfDate', {
            fieldType: 'date',
            sortable: true,
            filterable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'status', {
            sortable: true,
            filterable: true,
            requiredVisible: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'version', {
            fieldType: 'number',
            sortable: true,
            align: 'right',
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'totalLines', {
            fieldType: 'number',
            align: 'right',
            sortable: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'totalAmount', {
            fieldType: 'money',
            align: 'right',
            sortable: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'previewedBy', {
            sortable: false,
            searchable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'approvedBy', {
            sortable: false,
            searchable: true,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'approvedAt', {
            fieldType: 'datetime',
            sortable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'manifestHash', {
            sortable: false,
            searchable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'journalEntryId', {
            sortable: false,
            searchable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'supersedesBatchId', {
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'supersededByBatchId', {
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'reversedBy', {
            sortable: false,
            searchable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'reversedAt', {
            fieldType: 'datetime',
            sortable: true,
            defaultVisible: false,
        }),
        column(MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'reversalReason', {
            defaultVisible: false,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50] },
    defaultSort: { field: 'asOfDate', direction: 'desc' },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    capabilities: { ...standardCapabilities, create: true },
    pageActions: [
        importPageAction('action.admin.openingCutover.import', MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'CONFIDENTIAL', 'OPENING_CUTOVER'),
    ],
    toolbar: {
        showResultCount: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showRefresh: true,
        showCreateAction: true,
    },
    rowActivationAction: {
        ...metadata('action.openingCutover.view', MODULE_KEYS.ACCOUNTING, FEATURE_KEYS.ADMIN_OPENING_CUTOVER, 'CONFIDENTIAL'),
        operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_GET,
        labelKey: tk('action.view'),
        iconKey: 'view',
        kind: 'navigate',
        intent: 'neutral',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_DETAIL,
        successNavigateTo: routePath(ROUTE_IDS.ADMIN_OPENING_CUTOVER_DETAIL),
        errorBehavior: 'SHOW_TOAST_ERROR',
        group: 'navigation',
    },
};
export const EXPERIENCE_TABLE_REGISTRY = Object.freeze({
    [ORGANIZATION_TABLE.id]: ORGANIZATION_TABLE,
    'table.admin.organizationProfile': ORGANIZATION_TABLE,
    [USERS_TABLE.id]: USERS_TABLE,
    [ROLES_TABLE.id]: ROLES_TABLE,
    [AUDIT_TABLE.id]: AUDIT_TABLE,
    'table.admin.organizationAudit': AUDIT_TABLE,
    'table.admin.platformHealth': AUDIT_TABLE,
    'table.admin.platformSupportDelegations': AUDIT_TABLE,
    [INSTRUMENT_TABLE.id]: INSTRUMENT_TABLE,
    [POSITION_TRANSFER_TABLE.id]: POSITION_TRANSFER_TABLE,
    [SETTLEMENT_INSTRUCTION_TABLE.id]: SETTLEMENT_INSTRUCTION_TABLE,
    [CORPORATE_ACTION_TABLE.id]: CORPORATE_ACTION_TABLE,
    [FACT_BATCH_TABLE.id]: FACT_BATCH_TABLE,
    [IMPORT_JOB_TABLE.id]: IMPORT_JOB_TABLE,
    [RECONCILIATION_RUN_TABLE.id]: RECONCILIATION_RUN_TABLE,
    [ORGANIZATION_BRANCHES_TABLE.id]: ORGANIZATION_BRANCHES_TABLE,
    [MODULE_ASSIGNMENTS_TABLE.id]: MODULE_ASSIGNMENTS_TABLE,
    [ORGANIZATION_CLIENTS_TABLE.id]: ORGANIZATION_CLIENTS_TABLE,
    [ORGANIZATION_CLIENT_ACCOUNTS_TABLE.id]: ORGANIZATION_CLIENT_ACCOUNTS_TABLE,
    [ORGANIZATION_PORTFOLIOS_TABLE.id]: ORGANIZATION_PORTFOLIOS_TABLE,
    [LOTS_TABLE.id]: LOTS_TABLE,
    [DASHBOARD_HOLDINGS_TABLE.id]: DASHBOARD_HOLDINGS_TABLE,
    [DATA_CLIENTS_TABLE.id]: DATA_CLIENTS_TABLE,
    [DATA_PORTFOLIOS_TABLE.id]: DATA_PORTFOLIOS_TABLE,
    [DATA_POSITIONS_TABLE.id]: DATA_POSITIONS_TABLE,
    [CASH_POSITIONS_TABLE.id]: CASH_POSITIONS_TABLE,
    [DATA_JOURNALS_TABLE.id]: DATA_JOURNALS_TABLE,
    [DATA_FISCAL_PERIODS_TABLE.id]: DATA_FISCAL_PERIODS_TABLE,
    [DATA_FISCAL_YEARS_TABLE.id]: DATA_FISCAL_YEARS_TABLE,
    'table.admin.fiscalYears': DATA_FISCAL_YEARS_TABLE,
    [_PORTFOLIO_PERFORMANCE_TABLE.id]: _PORTFOLIO_PERFORMANCE_TABLE,
    [DATA_TAX_RECORDS_TABLE.id]: DATA_TAX_RECORDS_TABLE,
    [DATA_RISK_EXPOSURES_TABLE.id]: DATA_RISK_EXPOSURES_TABLE,
    [DATA_COMPLIANCE_BREACHES_TABLE.id]: DATA_COMPLIANCE_BREACHES_TABLE,
    [TAX_RULE_TABLE.id]: TAX_RULE_TABLE,
    [FEE_RULE_TABLE.id]: FEE_RULE_TABLE,
    [OPENING_CUTOVER_TABLE.id]: OPENING_CUTOVER_TABLE,
    [TRANSACTION_TABLE.id]: TRANSACTION_TABLE,
});
export function getExperienceTableDescriptor(id) {
    return EXPERIENCE_TABLE_REGISTRY[id];
}
export function getDashboardTableDescriptor(id) {
    return EXPERIENCE_TABLE_REGISTRY[id];
}
